/**
 * Y GO Shuttle Services France - Excursion Detail Page
 * Page dynamique pour afficher le détail d'une excursion
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  Clock, 
  Phone, 
  ChevronRight,
  ChevronLeft,
  Check,
  Mail,
  Calendar,
  MapPin,
  Users,
  Star,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import SafeImage from '../components/SafeImage';
import config from 'config';

const API_URL = config.API_URL;

// Category labels
const CATEGORY_LABELS = {
  escale_courte: 'Escale courte',
  escale_moyenne: 'Escale moyenne',
  journee_complete: 'Journée complète'
};

// Fallback images
const FALLBACK_IMAGES = {
  'etretat-falaises': 'https://images.unsplash.com/flagged/photo-1569750928069-a9e87dacd575?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxFdHJldGF0JTIwY2xpZmZzJTIwTm9ybWFuZHklMjBGcmFuY2UlMjBjb2FzdHxlbnwwfHx8fDE3Njg5MzYxNzl8MA&ixlib=rb-4.1.0&q=85',
  'honfleur-cote-fleurie': 'https://images.unsplash.com/photo-1704367630537-8d0dbc0d67ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxIb25mbGV1ciUyMGhhcmJvciUyME5vcm1hbmR5JTIwRnJhbmNlfGVufDB8fHx8MTc2ODkzNjE4MXww&ixlib=rb-4.1.0&q=85',
  'deauville-trouville': 'https://images.unsplash.com/photo-1675774196611-9a342a727e5f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxEZWF1dmlsbGUlMjBiZWFjaCUyMEZyYW5jZSUyMGx1eHVyeXxlbnwwfHx8fDE3Njg5MzYxODR8MA&ixlib=rb-4.1.0&q=85',
  'plages-debarquement-dday': 'https://images.unsplash.com/photo-1683375805925-4a896ce6e2f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxELURheSUyMGJlYWNoZXMlMjBOb3JtYW5keSUyMG1lbW9yaWFsJTIwRnJhbmNlfGVufDB8fHx8MTc2ODkzNjIwMXww&ixlib=rb-4.1.0&q=85',
  'mont-saint-michel': 'https://images.unsplash.com/photo-1505857807163-e4c177d2c3f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxNb250JTIwU2FpbnQlMjBNaWNoZWwlMjBGcmFuY2V8ZW58MHx8fHwxNzY4OTM2MTg4fDA&ixlib=rb-4.1.0&q=85',
  'rouen-ville-art': 'https://images.unsplash.com/photo-1560983073-c29bff7438ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&ixlib=rb-4.1.0&q=80',
};

export default function ExcursionDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [excursion, setExcursion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchExcursion = async () => {
      try {
        const response = await fetch(`${API_URL}/api/excursions/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setExcursion(data);
          
          // Update page title and meta
          document.title = data.meta_title || `${data.titre} | Excursion Y GO`;
          let metaDescription = document.querySelector('meta[name="description"]');
          if (!metaDescription) {
            metaDescription = document.createElement('meta');
            metaDescription.name = 'description';
            document.head.appendChild(metaDescription);
          }
          metaDescription.content = data.meta_description || data.description_courte;
        } else if (response.status === 404) {
          setError('Excursion non trouvée');
        } else {
          setError('Erreur de chargement');
        }
      } catch (err) {
        setError('Erreur de connexion');
      } finally {
        setLoading(false);
      }
    };
    
    fetchExcursion();
    
    return () => {
      document.title = 'Y GO Shuttle Services';
    };
  }, [slug]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#C9A24D] animate-spin" />
      </div>
    );
  }
  
  if (error || !excursion) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex flex-col items-center justify-center p-6">
        <h1 className="font-['Playfair_Display'] text-2xl text-[#E5E5E5] mb-4">
          {error || 'Excursion non trouvée'}
        </h1>
        <Button 
          onClick={() => navigate('/croisieres-excursions')}
          className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B]"
        >
          Retour aux excursions
        </Button>
      </div>
    );
  }
  
  const imageUrl = excursion.photo_principale || FALLBACK_IMAGES[slug] || 'https://images.unsplash.com/photo-1589802829985-817e51171b92?w=1200';
  
  return (
    <div data-testid="excursion-detail-page" className="min-h-screen bg-[#0B0B0B]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0B0B0B]/95 backdrop-blur-sm border-b border-[#C9A24D]/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link to="/" className="flex items-center">
              <span className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#C9A24D]">Y GO</span>
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/transferts" className="text-[#E5E5E5]/80 hover:text-[#C9A24D] text-sm font-medium transition-colors">
                Transferts
              </Link>
              <Link to="/croisieres-excursions" className="text-[#C9A24D] text-sm font-medium">
                Croisières & Excursions
              </Link>
              <Link to="/contact" className="text-[#E5E5E5]/80 hover:text-[#C9A24D] text-sm font-medium transition-colors">
                Devis & Contact
              </Link>
            </nav>
            
            <div className="flex items-center gap-4">
              <a href="tel:+33278780404" className="hidden sm:flex items-center gap-2 text-[#C9A24D] text-sm font-medium">
                <Phone className="w-4 h-4" />
                <span>+33 2 78 78 04 04</span>
              </a>
              <Link to="/contact">
                <Button className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold px-4 sm:px-6 py-2 text-sm rounded-none">
                  Devis gratuit
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>
      
      {/* Hero Section */}
      <section className="relative pt-20">
        <div className="relative h-[50vh] min-h-[400px]">
          <SafeImage 
            src={imageUrl}
            fallbackSrc="https://images.unsplash.com/photo-1589802829985-817e51171b92?w=1200"
            alt={excursion.titre}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/50 to-transparent" />
          <div className="absolute inset-0" style={{ backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', maskImage: 'linear-gradient(to top, black 0%, black 35%, rgba(0,0,0,0.4) 58%, transparent 75%)', WebkitMaskImage: 'linear-gradient(to top, black 0%, black 35%, rgba(0,0,0,0.4) 58%, transparent 75%)' }} />
          
          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
            <div className="container mx-auto">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-sm mb-6">
                <Link to="/" className="text-[#E5E5E5]/60 hover:text-[#C9A24D] transition-colors">
                  Accueil
                </Link>
                <ChevronRight className="w-4 h-4 text-[#E5E5E5]/40" />
                <Link to="/croisieres-excursions" className="text-[#E5E5E5]/60 hover:text-[#C9A24D] transition-colors">
                  Croisières & Excursions
                </Link>
                <ChevronRight className="w-4 h-4 text-[#E5E5E5]/40" />
                <span className="text-[#C9A24D]">{excursion.titre}</span>
              </nav>
              
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="flex items-center gap-2 bg-[#C9A24D]/20 px-3 py-1 text-[#C9A24D] text-sm">
                  <Clock className="w-4 h-4" />
                  {excursion.duree}
                </span>
                <span className="bg-[#1F1F1F] px-3 py-1 text-[#E5E5E5] text-sm">
                  {CATEGORY_LABELS[excursion.categorie] || excursion.categorie}
                </span>
              </div>
              
              <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#E5E5E5]">
                {excursion.titre}
              </h1>
            </div>
          </div>
        </div>
      </section>
      
      {/* Content Section */}
      <section className="py-12 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <div className="bg-[#1F1F1F] p-6 sm:p-8 border border-[#C9A24D]/20 mb-8">
                <p className="text-[#C9A24D] text-lg font-medium mb-4">
                  {excursion.description_courte}
                </p>
                <div className="text-[#E5E5E5]/80 leading-relaxed whitespace-pre-wrap">
                  {excursion.description}
                </div>
              </div>
              
              {/* Points forts */}
              {excursion.points_forts && excursion.points_forts.length > 0 && (
                <div className="bg-[#1F1F1F] p-6 sm:p-8 border border-[#C9A24D]/20 mb-8">
                  <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#E5E5E5] mb-6 flex items-center gap-2">
                    <Star className="w-5 h-5 text-[#C9A24D]" />
                    Points forts
                  </h2>
                  <ul className="space-y-3">
                    {excursion.points_forts.map((point, index) => (
                      <li key={index} className="flex items-start gap-3 text-[#E5E5E5]/80">
                        <Check className="w-5 h-5 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Options */}
              {excursion.options && excursion.options.length > 0 && (
                <div className="bg-[#1F1F1F] p-6 sm:p-8 border border-[#C9A24D]/20">
                  <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#E5E5E5] mb-6">
                    Options disponibles
                  </h2>
                  <ul className="space-y-3">
                    {excursion.options.map((option, index) => (
                      <li key={index} className="flex items-start gap-3 text-[#E5E5E5]/80">
                        <div className="w-2 h-2 bg-[#C9A24D] rounded-full flex-shrink-0 mt-2" />
                        <span>{option}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Booking Card */}
              <div className="bg-[#1F1F1F] p-6 border border-[#C9A24D]/20 sticky top-24">
                <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#E5E5E5] mb-4">
                  Réserver cette excursion
                </h3>
                
                {excursion.tarif_indicatif && (
                  <div className="mb-6">
                    <p className="text-[#E5E5E5]/60 text-sm mb-1">Tarif indicatif</p>
                    <p className="text-[#C9A24D] text-2xl font-bold">
                      {excursion.tarif_indicatif}
                    </p>
                  </div>
                )}
                
                <div className="space-y-3 mb-6 text-sm text-[#E5E5E5]/70">
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-[#C9A24D]" />
                    <span>Durée : {excursion.duree}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="w-4 h-4 text-[#C9A24D]" />
                    <span>Jusqu'à 8 passagers</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-[#C9A24D]" />
                    <span>Départ : Le Havre / Honfleur</span>
                  </div>
                </div>
                
                <Link to="/contact" className="block">
                  <Button className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold py-4 rounded-none mb-3">
                    <Mail className="w-4 h-4 mr-2" />
                    Demander un devis
                  </Button>
                </Link>
                
                <a href="tel:+33278780404" className="block">
                  <Button 
                    variant="outline" 
                    className="w-full border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10 py-4 rounded-none"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    +33 2 78 78 04 04
                  </Button>
                </a>
                
                <p className="text-[#E5E5E5]/50 text-xs text-center mt-4">
                  Devis gratuit sous 24h. Sans engagement.
                </p>
              </div>
            </div>
          </div>
          
          {/* Back link */}
          <div className="mt-12">
            <Link 
              to="/croisieres-excursions"
              className="inline-flex items-center text-[#C9A24D] hover:text-[#E6C77A] transition-colors"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Retour aux excursions
            </Link>
          </div>
        </div>
      </section>
      
      {/* Footer CTA */}
      <section className="py-16 bg-[#1F1F1F]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#E5E5E5] mb-4">
            Une autre destination en tête ?
          </h2>
          <p className="text-[#E5E5E5]/70 mb-8 max-w-xl mx-auto">
            Nous créons des itinéraires sur mesure pour vous faire découvrir la Normandie selon vos envies.
          </p>
          <Link to="/contact">
            <Button className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold px-8 py-4 rounded-none">
              Demander un itinéraire personnalisé
            </Button>
          </Link>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-8 bg-[#0B0B0B] border-t border-[#C9A24D]/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="inline-block mb-4">
            <span className="font-['Playfair_Display'] text-2xl font-bold text-[#C9A24D]">Y GO</span>
          </Link>
          <p className="text-[#E5E5E5]/60 text-sm">
            © 2024 Y GO Shuttle Services France. Tous droits réservés.
          </p>
        </div>
      </footer>
    </div>
  );
}
